import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  ContactResponse,
  ContactQuery
} from '@troia-v3/types';
import { contactsApi } from '../services/contactsApi';
import type { ContactFormData } from '../components/AddContactDialog';
import type { EditContactFormData } from '../components/EditContactDialog';

// Re-export Contact type para compatibilidade
export type Contact = ContactResponse;

/**
 * Hook para buscar lista de contatos
 *
 * @example
 * const { data, isLoading } = useContacts({ page: 1, limit: 50 });
 */
export const useContacts = (query?: ContactQuery, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['contacts', query],
    queryFn: ({ signal }) => contactsApi.getContacts(query, signal),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled,
  });
};

/**
 * Hook para buscar um contato específico
 */
export const useContact = (id?: string) => {
  return useQuery({
    queryKey: ['contact', id],
    queryFn: ({ signal }) => contactsApi.getContactById(id!, signal),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook para os dispositivos de um contato (aba "Dispositivos" do drawer).
 *
 * `enabled` é obrigatório no call-site para não disparar fetch com o drawer
 * fechado ou em aba inativa (regra de hooks em drawers do projeto).
 */
export const useContactDevices = (id?: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['contact-devices', id],
    queryFn: ({ signal }) => contactsApi.getContactDevices(id!, signal),
    enabled: !!id && enabled,
    staleTime: 60 * 1000,
  });
};

/**
 * Hook para criar novo contato com suporte a avatar
 *
 * @example
 * const { mutate: createContact, isPending } = useCreateContact();
 *
 * const handleSave = (data, avatar) => {
 *   createContact({ data, avatar }, {
 *     onSuccess: () => toast.success('Contato criado!'),
 *     onError: () => toast.error('Erro ao criar contato'),
 *   });
 * };
 */
export const useCreateContact = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data, avatar }: { data: ContactFormData; avatar?: File }) => {
      const formData = new FormData();

      // Nome obrigatório
      formData.append('name', data.name);

      // Identifiers como JSON string (multipart limitation)
      formData.append('identifiers', JSON.stringify({
        email: data.identifiers.email.filter(e => e.trim() !== ''),
        phone: data.identifiers.phone.filter(p => p.trim() !== '')
      }));

      // Tags como JSON string (sempre enviar, mesmo se vazio)
      if (data.tags !== undefined) {
        formData.append('tags', JSON.stringify(data.tags.filter(t => t.trim() !== '')));
      }

      // Cliente (Plano C) — só envia quando um customer foi selecionado;
      // "— Sem cliente —" (string vazia) na criação equivale a não vincular.
      if (data.customerId) {
        formData.append('customerId', data.customerId);
      }

      // Notas (texto livre) — só envia quando preenchidas
      if (data.notes && data.notes.trim() !== '') {
        formData.append('notes', data.notes.trim());
      }

      // Avatar (opcional)
      if (avatar) {
        formData.append('picture', avatar);
      }

      // PersonalInfo como JSON string
      if (data.personalInfo) {
        formData.append('personalInfo', JSON.stringify(data.personalInfo));
      }

      return contactsApi.createContactWithFile(formData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
};

/**
 * Hook para atualizar contato com suporte a avatar
 */
export const useUpdateContact = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data, avatar }: { id: string; data: EditContactFormData; avatar?: File }) => {
      const formData = new FormData();

      // Nome
      if (data.name) {
        formData.append('name', data.name);
      }

      // Identifiers como JSON string
      if (data.identifiers) {
        formData.append('identifiers', JSON.stringify({
          email: data.identifiers.email.filter(e => e.trim() !== ''),
          phone: data.identifiers.phone.filter(p => p.trim() !== '')
        }));
      }

      // Tags como JSON string (sempre enviar, mesmo se vazio)
      if (data.tags !== undefined) {
        formData.append('tags', JSON.stringify(data.tags.filter(t => t.trim() !== '')));
      }

      // Cliente (Plano C) — sempre envia (mesmo string vazia): o form sempre
      // inicializa `customerId` a partir do contato atual (`?? ''`), então
      // string vazia aqui é a escolha explícita "— Sem cliente —" e sinaliza
      // ao backend pra limpar o vínculo (`ContactsRepository.update`).
      if (data.customerId !== undefined) {
        formData.append('customerId', data.customerId);
      }

      // Notas — sempre envia (mesmo vazia): o form inicializa a partir do
      // contato atual, então string vazia é o usuário apagando a nota.
      if (data.notes !== undefined) {
        formData.append('notes', data.notes.trim());
      }

      // Avatar (opcional)
      if (avatar) {
        formData.append('picture', avatar);
      }

      // PersonalInfo como JSON string
      if (data.personalInfo) {
        formData.append('personalInfo', JSON.stringify(data.personalInfo));
      }

      return contactsApi.updateContactWithFile(id, formData);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['contact', variables.id] });
      // Tags têm chave PRÓPRIA (C8) — este update pode ter mexido em `data.tags`
      // (EditContactDialog também edita tags), então o header precisa recarregar.
      queryClient.invalidateQueries({ queryKey: ['contact', variables.id, 'tags'] });
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
};

/**
 * Hook para deletar contato
 */
export const useDeleteContact = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => contactsApi.deleteContact(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
};

/**
 * Hook para mergear contatos
 */
export const useMergeContacts = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sourceContactId, targetContactId, name }: {
      sourceContactId: string;
      targetContactId: string;
      name: string;
    }) => contactsApi.mergeContacts(sourceContactId, targetContactId, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
};

/**
 * Hook para criar conversa a partir de um contato
 */
export const useCreateConversationFromContact = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (contactId: string) => {
      // TODO: Implementar endpoint de criação de conversa a partir de contato no backend
      return Promise.resolve({
        conversationId: `conv-${contactId}-${Date.now()}`,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'], exact: false });
    },
  });
};

/**
 * Vincula/desvincula o contato a um Cliente — `PUT /contacts/:id` com
 * `{ customerId }`. String vazia é a sentinela de "limpar" que o
 * `ContactsRepository` já entende, e a partir da Task 4 o service mantém os
 * DOIS lados (contato e `customer.contacts[]`) em sincronia.
 *
 * Invalida `conversation`/`conversations` porque o nome do cliente vem
 * enriquecido dentro de `conversation.contact.customer` — sem isso o header
 * só atualizaria depois de recarregar a página.
 */
export const useSetContactCustomer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contactId, customerId }: { contactId: string; customerId: string }) =>
      contactsApi.updateContact(contactId, { customerId }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['contact', variables.contactId] });
      queryClient.invalidateQueries({ queryKey: ['contacts'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['conversation'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['conversations'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['customer'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['customers'], exact: false });
    },
  });
};
